import { print } from 'graphql';
import { getApiBase } from '@utils/runtimeEnv';
import { LOG_AUTH_AUDIT_EVENT } from './audit.gql';

export type AuthActivity = 'LOGIN' | 'LOGOUT';
export type LogoutReason = 'USER_INITIATED' | 'SESSION_TIMEOUT' | 'SESSION_EXPIRED';

const LOGOUT_REASONS: ReadonlySet<string> = new Set<LogoutReason>([
  'USER_INITIATED',
  'SESSION_TIMEOUT',
  'SESSION_EXPIRED',
]);

// handleLogout is also wired directly as an onClick handler, so its argument may be a
// click event rather than a reason — anything unrecognised counts as user-initiated.
export function toLogoutReason(value: unknown): LogoutReason {
  return typeof value === 'string' && LOGOUT_REASONS.has(value) ? (value as LogoutReason) : 'USER_INITIATED';
}

/**
 * Records a login/logout in the backend audit log.
 *
 * Sent with fetch rather than Apollo: login is recorded before ApolloWrapper mounts, and
 * logout clears storage and navigates away immediately — keepalive lets the request
 * outlive the page. Never throws: auditing must not block signing in or out.
 */
export async function logAuthActivity(
  activity: AuthActivity,
  accessToken: string | null,
  reason?: LogoutReason
): Promise<void> {
  if (!accessToken) return;
  try {
    const response = await fetch(`${getApiBase()}/graphql`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      // The backend ignores reason on LOGIN and defaults it to USER_INITIATED on LOGOUT.
      body: JSON.stringify({ query: print(LOG_AUTH_AUDIT_EVENT), variables: { activity, reason } }),
    });
    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[AUDIT] ${activity} audit returned HTTP ${response.status}`);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[AUDIT] Failed to log ${activity}:`, error);
  }
}
